"use client"

import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {useEffect} from "react";
import {APK_DOWNLOAD_PARAM, APK_DOWNLOAD_VALUE} from "@/lib/utils/appLink";
import {isAndroidDevice, isApkDownloadEnabled, startApkDownload} from "@/lib/utils/apkDownload";

/**
 * Landing side of a `?download=apk` link. The footer QR code used to be one;
 * it holds the apk-service link itself now, but codes scanned or saved earlier
 * and links sent around still land here. The attribution params are captured
 * by <RouteObserver/>, which is mounted before this one, so its effect has
 * already run. Here the one-shot `?download=apk` is consumed:
 * Android starts the APK download, other devices stay on the page. Either way the
 * param is dropped so a reload or a shared URL does not trigger it again.
 *
 * The download goes through apk-service (AC-900) via `startApkDownload`, which
 * reads the `ref`/`traffic` RouteObserver just stored — so the APK carries the
 * partner mark and a player who registers in the app keeps their partner.
 */
export default function ApkAutoDownload() {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const router = useRouter();
    const shouldDownload = searchParams.get(APK_DOWNLOAD_PARAM) === APK_DOWNLOAD_VALUE;

    useEffect(() => {
        if (!shouldDownload) return;

        if (isApkDownloadEnabled() && isAndroidDevice()) {
            startApkDownload();
        }

        const rest = new URLSearchParams(searchParams.toString());
        rest.delete(APK_DOWNLOAD_PARAM);
        const query = rest.toString();
        router.replace(query ? `${pathname}?${query}` : pathname, {scroll: false});
    }, [shouldDownload, searchParams, pathname, router]);

    return null;
}
